import * as _ from 'lodash';

import { LS_STATE_TAG, MainService } from 'app/main/MainService';
import { AppStateProvider } from 'app/shared/app_state/AppStateProvider';
import DebugService from '../debug.service';
import { ZoneCollection } from '../collections/zones/zone_collection.service';
import { AssetCollection } from '../collections/asset_collection.service';

const log = (...args) => DebugService.log('ObjectVisibility', ...args);

const enum VisibleTypes {
    ZONE = 'zone',
    ASSET = 'asset',
}

enum k_HIDDEN {
    Assets = 'imsvisibility_assets',
    Zones = 'imsvisibility_zones',
}

const DONT_COPY_STATE = false;

// Mutates a list.
function manageHiddenList(hiddenList: Set<number>, id: number, show: boolean): Set<number> {
    if (!show) {
        hiddenList.add(id);
    } else {
        hiddenList.delete(id);
    }
    return hiddenList;
}

// ImsObjectVisibilityService stores the hidden state of types.
// Everything is implicitly visible, we must explicitly hide things.
export class ImsObjectVisibilityService {
    private _state: AppStateProvider;
    private _cachedState: {};

    constructor(
        mainService: MainService,
        assetCollection: AssetCollection,
        zoneCollection: ZoneCollection,
    ) {
        // DRY functions to repeat setter logic by key.
        // Used when settings effects for state provider
        function setShow(key: k_HIDDEN) {
            return function (id: number, show: boolean) {
                return function (state) {
                    log(`Setting ${key} ${id} to be ${show ? 'shown' : 'hidden'}`);
                    const newList = new Set<number>(state[key] || []);
                    return Object.assign({}, state, {
                        [key]: Array.from(manageHiddenList(newList, id, show)),
                    });
                };
            };
        }

        function bulkSetShow(key: k_HIDDEN) {
            return function (ids: number[], show: boolean) {
                return function (state) {
                    log(`Setting ${ids.length} ${key} to be ${show ? 'shown' : 'hidden'}`);

                    let newList = new Set<number>(state[key] || []);
                    _.forEach(ids, (id) => {
                        newList = manageHiddenList(newList, id, show);
                    });
                    return Object.assign({}, state, {
                        [key]: Array.from(newList),
                    });
                };
            };
        }

        function showAll(key: k_HIDDEN) {
            return function () {
                return function (state) {
                    log(`Setting all ${key} to be shown`);
                    return Object.assign({}, state, {
                        [key]: [],
                    });
                };
            };
        }

        this._state = new AppStateProvider({
            parent: mainService.state || undefined,
            keys: {
                [k_HIDDEN.Assets]: {
                    initialValue: Array.from(mainService.initialStateValue(k_HIDDEN.Assets) || []),
                    defaultValue: [],
                    tags: [LS_STATE_TAG],
                },
                [k_HIDDEN.Zones]: {
                    initialValue: Array.from(mainService.initialStateValue(k_HIDDEN.Zones) || []),
                    defaultValue: [],
                    tags: [LS_STATE_TAG],
                },
            },
            effects: {
                setShowAsset: setShow(k_HIDDEN.Assets),
                setShowAssets: bulkSetShow(k_HIDDEN.Assets),
                showAllAssets: showAll(k_HIDDEN.Assets),
                setShowZone: setShow(k_HIDDEN.Zones),
                setShowZones: bulkSetShow(k_HIDDEN.Zones),
                showAllZones: showAll(k_HIDDEN.Zones),
            },
        });

        const updateCache = (state) => {
            this._cachedState = {
                [k_HIDDEN.Assets]: new Set(state[k_HIDDEN.Assets] || []),
                [k_HIDDEN.Zones]: new Set(state[k_HIDDEN.Zones] || []),
            };
        };

        // Initialize cached state.
        updateCache(this._state.getState());
        this.subscribeToChanges(updateCache);

        // When an asset is deleted, we need to trim it's key.
        assetCollection.addOnChangedListener((changed) => {
            const assetIDsToRemove = [];
            this.setShowAssets(assetIDsToRemove, true);
            Object.keys(changed).forEach((key) => {
                const asset = changed[key];
                if (asset.isDeleted) {
                    assetIDsToRemove.push(asset.id);
                }
            });
        });
        // When an asset is deleted, we need to trim it's key.
        zoneCollection.addOnChangedListener((changed) => {
            Object.keys(changed).forEach((key) => {
                const zone = changed[key];
                if (zone.isDeleted) {
                    this.setShowZone(zone.id, true);
                }
            });
        });
    }

    // Generic getter for visibility keys
    _getHidden(copyState: boolean, key: k_HIDDEN): Set<number> {
        if (copyState) {
            return new Set(this._state.getState()[key] || []);
        }

        return this._cachedState[key];
    }

    /**
     *
     * @param {boolean} copyState
     * @returns {Set<number>} Set containing the pk of hidden assets
     */
    getHiddenAssetIds(copyState: boolean = true): Set<number> {
        return this._getHidden(copyState, k_HIDDEN.Assets);
    }

    /**
     *
     * @param {boolean} copyState
     * @returns {Set<number>} Set containing the pk of hidden zones
     */
    getHiddenZoneIds(copyState: boolean = true): Set<number> {
        return this._getHidden(copyState, k_HIDDEN.Zones);
    }

    isShown(id: number, type: VisibleTypes): boolean {
        switch (type) {
            case VisibleTypes.ASSET:
                return this.isAssetShown(id);

            case VisibleTypes.ZONE:
                return this.isZoneShown(id);

            default:
                throw `${type} not supported for visibility`;
        }
    }

    isAssetShown(id: number): boolean {
        return !this.getHiddenAssetIds(DONT_COPY_STATE).has(id);
    }

    isZoneShown(id: number): boolean {
        return !this.getHiddenZoneIds(DONT_COPY_STATE).has(id);
    }

    async setShow(id: number, type: VisibleTypes, show: boolean) {
        if (typeof id === 'string') {
            throw new Error('Do not use string as an ID');
        }

        switch (type) {
            case VisibleTypes.ASSET:
                return this.setShowAsset(id, show);

            case VisibleTypes.ZONE:
                return this.setShowZone(id, show);

            default:
                throw `${type} not supported for visibility`;
        }
    }

    async setShowAsset(id: number, show: boolean) {
        if (typeof id === 'string') {
            throw new Error('Do not use string as an ID');
        }
        return this._state.effects.setShowAsset(id, show);
    }

    async showAllAssets() {
        return this._state.effects.showAllAssets();
    }

    async setShowAssets(ids: number[], show: boolean) {
        return this._state.effects.setShowAssets(ids, show);
    }

    async setShowZone(id: number, show: boolean) {
        if (typeof id === 'string') {
            throw new Error('Do not use string as an ID');
        }
        return this._state.effects.setShowZone(id, show);
    }

    async showAllZones() {
        return this._state.effects.showAllZones();
    }

    async setShowZones(ids: number[], show: boolean) {
        return this._state.effects.setShowZones(ids, show);
    }

    subscribe(fn, opts) {
        return this._state.onChange(fn, opts);
    }

    subscribeToChanges(fn) {
        return this._state.onChange(fn, {
            keys: [k_HIDDEN.Assets, k_HIDDEN.Zones],
        });
    }

    subscribeToAssetChanges(fn) {
        return this._state.onChange(fn, {
            keys: [k_HIDDEN.Assets],
        });
    }

    subscribeToZoneChanges(fn) {
        return this._state.onChange(fn, {
            keys: [k_HIDDEN.Zones],
        });
    }
}
